
import { Component, Inject, OnInit, OnChanges, SimpleChanges, Input } from '@angular/core';
import { APP_BASE_HREF } from '@angular/common';
import moment from 'moment-timezone';

import { FolderService } from '../../../Core/services/folder.service';
import { TagsService } from '../../../../Common/Tags/services/tags.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MonitorConfigService } from '../../../../Common/MonitorShared/services/monitor-config.service';
import { TagGroupsService } from '../../../../Common/Tags/services/tag-groups.service';
import { MediaSubscriptionService } from '../../../Core/services/media-subscription.service';
import { SourceGroupService } from '../../../../Common/Sources/services/source-group.service';
import { SourceService } from '../../../../Common/Sources/services/source.service';
import { ThemeService } from '../../../../Social/Intelligence/services/theme.service';
import { AgentService } from '../../../../Social/Intelligence/services/agent.service';
import { MentionSearchService } from '../../../Core/services/mention-search.service';
import { TopicsService } from '../../../Core/services/topics.service';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { MonitoringSourcePickerFieldWithFacetComponent } from './monitoring-source-picker-field-with-facet.component';
import { MentionSearchSettingService } from '../../../../Common/MonitorShared/services/mention-search-setting.service';
import { skip } from 'rxjs';
import { DateQueryModel } from '@Common/Forms/models/date-query.model';
import { MonitoringSeriesTypeEnum } from '@Monitor/Shared/enums/monitoring-series-type.enum';
import { defaultDateTypeOptions, savedSourceDateTypeOptions } from '@Common/Forms/components/DateRangeSelector/date-range-selector.component';
import {
    getMonitoringDateQueryInputFolderOptions,
    getMonitoringDateQueryInputTopicOptions
} from '@Monitor/Shared/models/monitoring-date-query-input-options';
import { RelativeTimezoneDateTypeEnum } from '@api/mentions/enum/relative-timezone-date-type.enum';
import { DateQueryTypeEnum } from '@api/mentions/enum/date-query-type.enum';
import { DateInputTypesEnum } from '../../../../Common/Forms/enums/date-input-types.enum';
import { CachedDateService } from '../../../../Common/MonitorShared/services/cached-date.service';
import { DropdownOptionModel } from '@Common/Forms/models/dropdown-option.model';
import { QuarterlyDateRangeSettingsModel, QuarterOptionEnum } from '@Monitor/Shared/models/quarterly-date-range-settings.model';
import { MonthlyDateRangeSettingsModel, MonthOptionEnum, monthOptionLocalizationKeys } from '@Monitor/Shared/models/monthly-date-range-settings.model';

/**
 * This component is designed to work with pagebuilder as well as being used on its own.
 *
 * It will allow the user to select a datasource from the Monitoring Feature module
 * and emit an event with the type of datasource selected along with the id of the relevant datasource.
 *
 * In the event this component is used with pageBuilders "blockform" component this component will
 * emit the event which will be added to display element of the relevant block.
 */
@Component({
    selector: 'agpr-monitoring-source-picker-with-facet-and-search-field',
    templateUrl: './monitoring-source-picker-field.component.html',
    styleUrls: ['./monitoring-source-picker-field.component.scss'],
    providers: [
        MentionSearchService,
        {
            provide: FilterService,
            useExisting: MentionSearchService
        }
    ],
    standalone: false
})
export class MonitoringSourcePickerFieldWithFacetAndSearchComponent extends MonitoringSourcePickerFieldWithFacetComponent implements OnInit, OnChanges {
    public readonly messageSeverityEnum = messageSeverityEnum;
    public readonly monthOptionEnum = MonthOptionEnum;
    public readonly quarterOptionEnum = QuarterOptionEnum;

    @Input() isGlobalDateRange: boolean = false;

    @Input() monthlyDateRange: boolean = false;
    @Input() monthlySettings: MonthlyDateRangeSettingsModel = new MonthlyDateRangeSettingsModel();
    @Input() restrictMonthToMostRecent: boolean = false;

    /** When true, the date-range slot renders the quarterly (calendar/fiscal quarter) UI instead of the date-query-popup. */
    @Input() quarterlyDateRange: boolean = false;
    /** Selection state for the quarterly UI; mutated by reference so the parent can read it back. */
    @Input() quarterlySettings: QuarterlyDateRangeSettingsModel = new QuarterlyDateRangeSettingsModel();
    /** When true (e.g. recurring delivery), only the "most recent quarter" option is offered. */
    @Input() restrictQuarterToMostRecent: boolean = false;

    hasUnsavedSourceType: boolean = false;

    readonly calendarMonthOptions: DropdownOptionModel[] = Object.values(MonthOptionEnum).map(
        value => new DropdownOptionModel(monthOptionLocalizationKeys[value], value)
    );

    readonly calendarQuarterOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.mostRecentCalendarQuarter', QuarterOptionEnum.MOST_RECENT),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.q1', QuarterOptionEnum.Q1),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.q2', QuarterOptionEnum.Q2),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.q3', QuarterOptionEnum.Q3),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.q4', QuarterOptionEnum.Q4),
    ];
    readonly fiscalQuarterOptions: DropdownOptionModel[] = [
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.mostRecentFiscalQuarter', QuarterOptionEnum.MOST_RECENT),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.fiscalQ1', QuarterOptionEnum.Q1),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.fiscalQ2', QuarterOptionEnum.Q2),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.fiscalQ3', QuarterOptionEnum.Q3),
        new DropdownOptionModel('Briefing.onlineBriefing.settings.dateRange.fiscalQ4', QuarterOptionEnum.Q4),
    ];
    yearOptions: DropdownOptionModel[] = [];
    monthYearOptions: DropdownOptionModel[] = [];
    effectiveDateFilterOptions: DropdownOptionModel[] = [];

    constructor(
        public topicsService: TopicsService,
        public monitorConfigService: MonitorConfigService,
        public folderService: FolderService,
        public tagsService: TagsService,
        public tagGroupsService: TagGroupsService,
        public mediaSubscriptionService: MediaSubscriptionService,
        public sourceGroupService: SourceGroupService,
        public sourceService: SourceService,
        public themeService: ThemeService,
        public agentService: AgentService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
        public mentionSearchService: MentionSearchService,
        public localizationService: LocalizationService,
        public currentUserStateService: CurrentUserStateService,
        private mentionSearchSettingService: MentionSearchSettingService,
        public cachedDateService: CachedDateService
    ) {
        super(
            topicsService,
            monitorConfigService,
            folderService,
            tagsService,
            tagGroupsService,
            mediaSubscriptionService,
            sourceGroupService,
            sourceService,
            themeService,
            agentService,
            appBaseHref,
            mentionSearchService,
            localizationService,
            currentUserStateService,
            cachedDateService
        );
    }

    ngOnInit() {
        // don't allow grouping
        this.mentionSearchService.duplicates.showGroup = false;
        // for global date range pickers, start with whatever the parent has already set
        this.mentionSearchService.setDateQuery(
            this.isGlobalDateRange
                ? this.mentionSearchSettingService.currentDateQuery
                : DateQueryModel.getAllQuery()
        );
        // initialize the default search criteria
        this.updateSearchCriteria();
        this.setupSubscriptions();
        this.initializeDataSourceOptions();

        // HACK: Use skip(1) to skip the data source's default value (folder type, id=null).
        this.subscriptions.push(
            this.mentionSearchSettingService.dateQuery$.pipe(skip(1)).subscribe(x => {
                if (x) {
                    this.dateQuery = x;
                    if (!this.isGlobalDateRange) {
                        this.dateRangeChanged(this.dateQuery);
                    }
                }
            }),
            this.mentionSearchSettingService.selectType$.pipe(skip(1)).subscribe(type => {
                if (!this.sourceIsValid()) {
                    this.sourceDropdownOption = this.field.dataSourceOptions.find(option => option.value === type);
                    this.selectType(type);
                }
            }),
            this.mentionSearchSettingService.hasUnsavedSourceType$.subscribe(x => {
                if (this.isGlobalDateRange && this.hasUnsavedSourceType !== x) {
                    this.hasUnsavedSourceType = x;
                    this.updateDateTypeOptions();
                    this.updateDateFilterOptions();
                }
            }),
            // re-apply the quarterly date query when the source type changes (e.g. driven by scheduling)
            this.mentionSearchSettingService.selectType$.subscribe(() => {
                if (this.quarterlyDateRange) {
                    setTimeout(() => this.quarterSelectionChanged());
                }
            }),
            this.mentionSearchSettingService.selectType$.subscribe(() => {
                if (this.monthlyDateRange && this.monthlySettings?.isCalendarMonths) {
                    setTimeout(() => this.monthSelectionChanged());
                }
            })
        );

        if (this.monthlyDateRange) {
            this.monthlySettings.selectedMonth ??= MonthOptionEnum.MOST_RECENT;
            this.refreshMonthYearOptions();
            this.subscriptions.push(
                this.monitorConfigService.getSearchRange().subscribe(searchRange => {
                    this.searchRange = searchRange;
                    if (this.monthlySettings.isCalendarMonths) {
                        // Month mode → refresh year options + apply month query
                        setTimeout(() => this.monthSelectionChanged());
                    } else {
                        // Custom mode → clamp date range
                        setTimeout(() => this.sanitizeMonthlyCustomDateRange());
                    }
                })
            );

            if (this.monthlySettings.isCalendarMonths) {
                setTimeout(() => this.monthSelectionChanged());
            } else {
                const existingQuery = this.value?.filterSearchCriteria?.query?.dateQuery;
                if (existingQuery) {
                    this.dateQuery = existingQuery;
                }
            }
        }

        if (this.quarterlyDateRange) {
            this.quarterlySettings.selectedQuarter ??= this.calendarQuarterOptions[0].value;
            this.refreshYearOptions();
            // the topic search-limit arrives asynchronously; re-evaluate the valid years once it loads
            this.subscriptions.push(
                this.monitorConfigService.getSearchRange().subscribe(searchRange => {
                    if (searchRange) {
                        setTimeout(() => this.quarterSelectionChanged());
                    }
                })
            );
            setTimeout(() => this.quarterSelectionChanged());
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        // preserve the base behavior (re-initializes the displayed source when the `value` input changes,
        // e.g. when a brand's preferred topic is applied programmatically)
        super.ngOnChanges(changes);

        // In quarterly mode the date range is owned by the quarter radio buttons, not the source's own
        // saved date query. When a new source is applied (e.g. a brand's preferred topic populates the
        // picker), re-apply the selected quarter so it isn't overwritten by the topic's date range.
        // Deferred so it runs after ngOnInit has initialized the quarterly settings on the first cycle.
        if (changes['value'] && this.quarterlyDateRange) {
            setTimeout(() => this.quarterSelectionChanged());
        }

        if (changes['value'] && this.monthlyDateRange && this.monthlySettings?.isCalendarMonths) {
            setTimeout(() => this.monthSelectionChanged());
        }

        if (
            changes['restrictMonthToMostRecent']
            && this.monthlyDateRange
            && this.restrictMonthToMostRecent
            && this.monthlySettings?.selectedMonth !== MonthOptionEnum.MOST_RECENT
        ) {
            this.monthlySettings.selectedMonth = MonthOptionEnum.MOST_RECENT;
            this.monthSelectionChanged();
        }

        // when delivery becomes recurring, only "most recent quarter" is valid – reset off any specific quarter
        if (
            changes['restrictQuarterToMostRecent']
            && this.quarterlyDateRange
            && this.restrictQuarterToMostRecent
            && this.quarterlySettings?.selectedQuarter !== QuarterOptionEnum.MOST_RECENT
        ) {
            const options = this.quarterlySettings.isCalendarQuarters ? this.calendarQuarterOptions : this.fiscalQuarterOptions;
            this.quarterlySettings.selectedQuarter = options[0].value;
            this.quarterSelectionChanged();
        }

        if (changes['restrictMonthToMostRecent']) {
            this.updateEffectiveDateFilterOptions();
            if (this.restrictMonthToMostRecent && this.monthlyDateRange && !this.monthlySettings.isCalendarMonths) {
                const lastMonthQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_MONTH);
                lastMonthQuery.timezone = this.timezone;
                this.dateQuery = lastMonthQuery;
                this.dateRangeChanged(lastMonthQuery);
            }
        }
    }

    /**
     * Handles change in date range
     * @param dateQuery -  the date to change the selected date to
     */
    public async dateRangeChanged(dateQuery: DateQueryModel): Promise<void> {
        if (this.isGlobalDateRange) {
            this.mentionSearchSettingService.updateDateQuery(dateQuery);
        }

        if (this.value?.filterSearchCriteria?.query) {
            this.value.filterSearchCriteria.query.dateQuery = dateQuery;
        }

        super.dateRangeChanged(dateQuery);
    }

    public updateDateTypeOptions(): void {
        if (!this.isGlobalDateRange) {
            return;
        }

        if (this.hasUnsavedSourceType) {
            this.dateTypeOptions = defaultDateTypeOptions;
        } else {
            this.dateTypeOptions = savedSourceDateTypeOptions;
        }
    }

    /**
         * Updates the datefilter options based on the type of datasource selected
         */
    public async updateDateFilterOptions(isInitialization: boolean = false): Promise<void> {
        if (!this.isGlobalDateRange && !isInitialization) {
            return;
        }

        if (this.hasUnsavedSourceType) {
            this.dateFilterOptions = getMonitoringDateQueryInputTopicOptions();
        } else {
            this.dateFilterOptions = getMonitoringDateQueryInputFolderOptions();
        }

        if (isInitialization && this.dateQuery?.type === DateQueryTypeEnum.ABSOLUTE) {
            return;
        }

        // backwards compatibility layer for old relative time
        // if date query is 'last 24 hours', 'last 3 days', 'last 7 days', 'last 30 days', convert it to the correct relative timezone query
        if (
            this.dateQuery?.type === DateQueryTypeEnum.RELATIVE &&
                this.dateQuery?.relativeStartOffsetH === -24
                || this.dateQuery?.relativeStartOffsetH === -72
                ||  this.dateQuery?.relativeStartOffsetH === -168
                ||  this.dateQuery?.relativeStartOffsetH === -720

        ) {
            let relativeTimezoneQuery: DateQueryModel = new DateQueryModel();
            relativeTimezoneQuery.timezone = this.timezone;
            switch (this.dateQuery.relativeStartOffsetH) {
                case -24:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_24_HOURS);
                    break;
                case -72:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_3_DAYS);
                    break;
                case -168:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_7_DAYS);
                    break;
                case -720:
                    relativeTimezoneQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_30_DAYS);
                    break;
            }
            relativeTimezoneQuery.dateType = this.dateQuery.dateType;
            this.dateQuery = relativeTimezoneQuery;
        }

        const optionInList = this.dateFilterOptions.find((option) => {
            return DateQueryModel.equals(option.value.query, this.dateQuery);
        });

        // We changed the options in the list, if the option chosen is no longer available, choose the first option in the list
        if (!optionInList) {
            const savedDate = this.value?.filterSearchCriteria?.query?.dateQuery as DateQueryModel | null;
            const savedInList = savedDate
                ? this.dateFilterOptions.find(opt => DateQueryModel.equals(opt.value.query, savedDate))
                : null;
            if (savedInList) {
                // Restore the saved date silently (no dateRangeChanged) to avoid overwriting persisted state during init
                this.dateQuery = savedInList.value.query;
                this.dateQuery.timezone = this.timezone;
                this.setSearchDateQuery(this.dateQuery);
            } else if (savedDate?.type === DateQueryTypeEnum.ABSOLUTE) {
                // Absolute date ranges are not preset options — preserve them as-is
                this.dateQuery = savedDate;
                this.setSearchDateQuery(this.dateQuery);
            } else {
                this.dateQuery = this.dateFilterOptions[0].value.query;
                this.dateQuery.timezone = this.timezone;
                await this.dateRangeChanged(this.dateFilterOptions[0].value.query);
            }
        }
    }

    /**
     * Selects a type of series
     */
    public async selectType(type: MonitoringSeriesTypeEnum): Promise<void> {
        this.sourceType = type;
        this.selectedSource = null;
        this.resetFilters();

        this.value.type = this.sourceType;
        this.value.id = null;

        this.setAccountFilters();
        await this.updateSearchCriteria();
        this.clearSearch();

        this.search();

        this.emitSuggestions();

        // a new source type changes which years are valid (topic search-limit vs saved coverage)
        if (this.quarterlyDateRange) {
            this.quarterSelectionChanged();
        } else if (this.monthlyDateRange) {
            if (this.monthlySettings?.isCalendarMonths) {
                // Month mode: re-apply monthly date query so year options refresh for the new source
                this.monthSelectionChanged();
            } else {
                // Custom mode: if the chosen range falls entirely outside the new source's
                // search window, reset to last month; otherwise sanitize as usual
                this.sanitizeMonthlyCustomDateRange();
            }
        } else {
            // topic/subscription sources can't be searched with an "all" date query (search() bails out
            // and never hits the backend). Sanitize the date to a valid range here — mirrors the base
            // class. No-op for saved/account sources and for cards without their own date UI but a valid
            // inherited date. setSafeTopicDateQuery() re-runs the search via dateRangeChanged() when it
            // adjusts the date.
            this.setSafeTopicDateQuery();
        }

        this.emitChanges();
    }

    get effectiveCalendarQuarterOptions(): DropdownOptionModel[] {
        return this.restrictQuarterToMostRecent ? [this.calendarQuarterOptions[0]] : this.calendarQuarterOptions;
    }

    get effectiveFiscalQuarterOptions(): DropdownOptionModel[] {
        return this.restrictQuarterToMostRecent ? [this.fiscalQuarterOptions[0]] : this.fiscalQuarterOptions;
    }

    /** Bridges the dropdown's option model and the raw quarter value stored on the settings. */
    get selectedQuarterOption(): DropdownOptionModel | null {
        const options = this.quarterlySettings.isCalendarQuarters ? this.calendarQuarterOptions : this.fiscalQuarterOptions;
        return options.find(option => option.value === this.quarterlySettings.selectedQuarter) ?? null;
    }
    set selectedQuarterOption(option: DropdownOptionModel | null) {
        this.quarterlySettings.selectedQuarter = option?.value ?? null;
    }

    /** Bridges the dropdown's option model and the raw year value stored on the settings. */
    get selectedYearOption(): DropdownOptionModel | null {
        return this.yearOptions.find(option => option.value === this.quarterlySettings.selectedYear) ?? null;
    }
    set selectedYearOption(option: DropdownOptionModel | null) {
        this.quarterlySettings.selectedYear = option?.value ?? null;
    }

    get effectiveCalendarMonthOptions(): DropdownOptionModel[] {
        return this.restrictMonthToMostRecent ? [this.calendarMonthOptions[0]] : this.calendarMonthOptions;
    }

    get selectedMonthOption(): DropdownOptionModel | null {
        const options = this.monthlySettings.isCalendarMonths ? this.calendarMonthOptions : this.fiscalQuarterOptions;
        return options.find(option => option.value === this.monthlySettings.selectedMonth) ?? null;
    }
    set selectedMonthOption(option: DropdownOptionModel | null) {
        this.monthlySettings.selectedMonth = option?.value ?? null;
    }

    /** Bridges the dropdown's option model and the raw year value stored on the settings. */
    get selectedMonthYearOption(): DropdownOptionModel | null {
        return this.monthYearOptions.find(option => option.value === this.monthlySettings.selectedYear) ?? null;
    }
    set selectedMonthYearOption(option: DropdownOptionModel | null) {
        this.monthlySettings.selectedYear = option?.value ?? null;
    }

    monthSelectionChanged(): void {
        this.refreshMonthYearOptions();
        const query = this.getMonthlyDateQuery();
        this.dateRangeChanged(query);
    }

    monthTypeChanged(): void {
        if (this.monthlySettings.isCalendarMonths) {
            this.monthSelectionChanged();
        } else {
            const lastMonthQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_MONTH);
            lastMonthQuery.timezone = this.timezone;
            this.dateQuery = lastMonthQuery;
            this.dateRangeChanged(lastMonthQuery);
        }
    }

    quarterTypeChanged(): void {
        const options = this.quarterlySettings.isCalendarQuarters ? this.calendarQuarterOptions : this.fiscalQuarterOptions;
        this.quarterlySettings.selectedQuarter = options[0].value;
        this.quarterSelectionChanged();
    }

    quarterSelectionChanged(): void {
        this.refreshYearOptions();
        this.dateRangeChanged(this.getQuarterlyDateQuery());
    }

    refreshYearOptions(): void {
        const currentYear = new Date().getFullYear();
        // an unsaved source (topic, subscription, social topic, theme, etc.) searches live coverage,
        // so its years are bound by the searchable coverage window
        const isUnsavedSource = this.value?.isUnsavedSource() ?? false;

        // never offer a quarter that has not occurred yet
        const latestValidYear = this.getLatestValidYearForQuarter();

        let earliestYear: number;
        let latestYear: number;
        if (isUnsavedSource && this.searchRange?.from) {
            // unsaved source: only years within the searchable coverage window are valid
            earliestYear = this.searchRange.from.getFullYear();
            const searchableLatest = this.searchRange.to ? this.searchRange.to.getFullYear() : currentYear;
            latestYear = Math.min(searchableLatest, latestValidYear);
        } else {
            // saved coverage (or an unsaved source before its search limit has loaded): current and previous 10 years
            earliestYear = currentYear - 10;
            latestYear = latestValidYear;
        }

        const newOptions: DropdownOptionModel[] = [];
        for (let year = latestYear; year >= earliestYear; year--) {
            newOptions.push(new DropdownOptionModel(String(year), String(year)));
        }
        this.yearOptions = newOptions;

        const stillValid = this.quarterlySettings.selectedYear
            && this.yearOptions.some(y => y.value === this.quarterlySettings.selectedYear);
        if (!stillValid) {
            // auto-select the most current valid year for the chosen quarter
            this.quarterlySettings.selectedYear = this.yearOptions[0]?.value ?? null;
        }
    }

    refreshMonthYearOptions(): void {
        const currentYear = new Date().getFullYear();
        // an unsaved source (topic, subscription, etc.) searches live coverage,
        // so its years are bound by the searchable coverage window
        const isUnsavedSource = this.value?.isUnsavedSource() ?? false;

        // never offer a month that has not occurred yet
        const latestValidYear = this.getLatestValidYearForMonth();

        let earliestYear: number;
        let latestYear: number;
        if (isUnsavedSource && this.searchRange?.from) {
            // unsaved source: only years within the searchable coverage window are valid
            earliestYear = this.searchRange.from.getFullYear();
            const searchableLatest = this.searchRange.to ? this.searchRange.to.getFullYear() : currentYear;
            latestYear = Math.min(searchableLatest, latestValidYear);
        } else {
            // saved coverage (or unsaved source before search limit loaded): current and previous 10 years
            earliestYear = currentYear - 10;
            latestYear = latestValidYear;
        }

        const newOptions: DropdownOptionModel[] = [];
        for (let year = latestYear; year >= earliestYear; year--) {
            newOptions.push(new DropdownOptionModel(String(year), String(year)));
        }
        this.monthYearOptions = newOptions;

        const stillValid = this.monthlySettings.selectedYear
            && this.monthYearOptions.some(y => y.value === this.monthlySettings.selectedYear);
        if (!stillValid) {
            this.monthlySettings.selectedYear = this.monthYearOptions[0]?.value ?? null;
        }
    }

    /**
     * The latest year selectable for the currently chosen quarter — a quarter is only offered for the
     * current year once that quarter has actually started; otherwise it tops out at the previous year.
     */
    private getLatestValidYearForQuarter(): number {
        const currentYear = new Date().getFullYear();
        const quarter = this.quarterlySettings.selectedQuarter;
        if (!quarter || quarter === QuarterOptionEnum.MOST_RECENT) {
            return currentYear;
        }
        const q = +String(quarter).replace('Q', '');
        if (isNaN(q) || q < 1 || q > 4) {
            return currentYear;
        }
        const [start] = this.quarterlySettings.isCalendarQuarters
            ? this.getCalendarQuarterDates(q, currentYear)
            : this.getFiscalQuarterDates(q, currentYear);
        const now = this.timezone ? moment().tz(this.timezone).toDate() : new Date();
        return now >= start ? currentYear : currentYear - 1;
    }

    private getQuarterlyDateQuery(): DateQueryModel {
        if (this.quarterlySettings.selectedQuarter === QuarterOptionEnum.MOST_RECENT) {
            const query = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_QUARTER);
            query.timezone = this.timezone;
            return query;
        }

        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        const q = +(this.quarterlySettings.selectedQuarter ?? '').replace('Q', '');
        const year = +(this.quarterlySettings.selectedYear ?? '');
        if (!isNaN(q) && !isNaN(year) && q >= 1 && q <= 4) {
            const [start, end] = this.quarterlySettings.isCalendarQuarters
                ? this.getCalendarQuarterDates(q, year)
                : this.getFiscalQuarterDates(q, year);
            dateQuery.absoluteRange = { start, end };
        }
        return dateQuery;
    }

    private getCalendarQuarterDates(q: number, year: number): [Date, Date] {
        const startMonth = (q - 1) * 3;
        if (this.timezone) {
            const tz = this.timezone;
            return [
                moment().tz(tz).year(year).month(startMonth).date(1).startOf('day').toDate(),
                moment().tz(tz).year(year).month(startMonth + 2).date(1).endOf('month').toDate()
            ];
        }
        return [new Date(year, startMonth, 1), new Date(year, startMonth + 3, 0)];
    }

    private getFiscalQuarterDates(q: number, year: number): [Date, Date] {
        const fiscalStartMonths = [9, 0, 3, 6]; // FQ1: Oct, FQ2: Jan, FQ3: Apr, FQ4: Jul
        const startMonth = fiscalStartMonths[q - 1];
        if (this.timezone) {
            const tz = this.timezone;
            return [
                moment().tz(tz).year(year).month(startMonth).date(1).startOf('day').toDate(),
                moment().tz(tz).year(year).month(startMonth + 2).date(1).endOf('month').toDate()
            ];
        }
        return [new Date(year, startMonth, 1), new Date(year, startMonth + 3, 0)];
    }

    private getLatestValidYearForMonth(): number {
        const currentYear = new Date().getFullYear();
        const month = this.monthlySettings.selectedMonth;
        if (!month || month === MonthOptionEnum.MOST_RECENT) {
            return currentYear;
        }
        // M1 → 0, M2 → 1, ... M12 → 11
        const monthIndex = parseInt(String(month).replace('M', ''), 10) - 1;
        if (isNaN(monthIndex) || monthIndex < 0 || monthIndex > 11) {
            return currentYear;
        }
        // Start of selected month in current year
        const [start] = this.getCalendarMonthDates(monthIndex, currentYear);
        const now = this.timezone ? moment().tz(this.timezone).toDate() : new Date();
        return now >= start ? currentYear : currentYear - 1;
    }

    private getMonthlyDateQuery(): DateQueryModel {
        if (this.monthlySettings.selectedMonth === MonthOptionEnum.MOST_RECENT) {
            const query = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_MONTH);
            query.timezone = this.timezone;
            return query;
        }

        const dateQuery = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        const month = parseInt((this.monthlySettings.selectedMonth ?? '').replace('M', ''), 10) - 1; // ← fix
        const year = +(this.monthlySettings.selectedYear ?? '');
        if (!isNaN(month) && !isNaN(year) && month >= 0 && month <= 11) {
            const [start, end] = this.getCalendarMonthDates(month, year);
            dateQuery.absoluteRange = { start, end };
        }
        return dateQuery;
    }

    private getCalendarMonthDates(monthIndex: number, year: number): [Date, Date] {
        if (this.timezone) {
            const tz = this.timezone;
            return [
                moment().tz(tz).year(year).month(monthIndex).date(1).startOf('day').toDate(),
                moment().tz(tz).year(year).month(monthIndex).date(1).endOf('month').toDate()
            ];
        }
        return [new Date(year, monthIndex, 1), new Date(year, monthIndex + 1, 0)];
    }

    private updateEffectiveDateFilterOptions(): void {
        const optionsExcludingAll = this.dateFilterOptions.filter(
            option => option.value?.query?.type !== DateQueryTypeEnum.ALL
        );
        if (!this.restrictMonthToMostRecent) {
            this.effectiveDateFilterOptions = optionsExcludingAll;
            return;
        }
        const lastMonthOption = this.dateFilterOptions.find(
            option => option.value?.query?.relativeTimezoneDateType === RelativeTimezoneDateTypeEnum.LAST_MONTH
        );
        this.effectiveDateFilterOptions = lastMonthOption ? [lastMonthOption] : this.dateFilterOptions;
    }

    private sanitizeMonthlyCustomDateRange(): void {
        if (!this.searchRange || !this.dateQuery?.absoluteRange) {
            this.setSafeTopicDateQuery();
            return;
        }
        if (this.dateQuery.absoluteRange.end < this.searchRange.from) {
            const lastMonthQuery = DateQueryModel.getRelativeTimezoneQuery(RelativeTimezoneDateTypeEnum.LAST_MONTH);
            lastMonthQuery.timezone = this.timezone;
            this.dateRangeChanged(lastMonthQuery);
            return;
        }
        if (this.dateQuery.absoluteRange.start < this.searchRange.from) {
            const clampedQuery = DateQueryModel.clone(this.dateQuery);
            clampedQuery.absoluteRange = {
                start: this.searchRange.from,
                end: this.dateQuery.absoluteRange.end
            };
            this.dateQuery = clampedQuery;
            this.dateRangeChanged(clampedQuery);
        }
    }

}
